import {LAYER_TYPES, LayerOptions} from '../../../../../shared/image-layers/layer.types';
import {BaseLayer} from '../../../../../shared/image-layers/layers/BaseLayer';
import {action, observable} from 'mobx';
import {ICON_PATHS} from '../../icons';
import {MeshCapablePointOfInterest, filterCapturedPointsOfInterest, isMeshPoint} from '../../../mesh-points.utils';

const BASE_ICON_SIZE = 24;
const MIN_ICON_SIZE = 12;
const DEFAULT_ICON_COLOR = '#FFFFFF';

let cachedMeshPath: Path2D | undefined;

interface MeshPointMarker {
    x: number;
    y: number;
    color: string;
}

export interface MeshPointsLayerOptions extends LayerOptions {
    id?: string;
}

export class MeshPointsLayer extends BaseLayer {
    @observable private markers: MeshPointMarker[] = [];

    private sourceWidth: number = 0;

    private sourceHeight: number = 0;

    constructor(options?: MeshPointsLayerOptions) {
        super(options?.id ?? 'mesh-points', LAYER_TYPES.MESH_POINTS, {
            visible: options?.visible ?? true,
            opacity: options?.opacity ?? 1,
            zIndex: options?.zIndex ?? 1
        });
    }

    @action
    setMeshPoints(
        pointsOfInterest: MeshCapablePointOfInterest[] = [],
        sourceWidth?: number,
        sourceHeight?: number
    ): void {
        this.sourceWidth = sourceWidth ?? 0;
        this.sourceHeight = sourceHeight ?? 0;
        this.markers = filterCapturedPointsOfInterest(pointsOfInterest)
            .filter(isMeshPoint)
            .map((poi) => ({
                x: poi.coordinates!.x,
                y: poi.coordinates!.y,
                color: poi.color || DEFAULT_ICON_COLOR
            }));

        this.invalidate();
    }

    render(ctx: CanvasRenderingContext2D, containerWidth: number, containerHeight: number): void {
        if (!this.markers.length || !this.sourceWidth || !this.sourceHeight) return;

        // POI coordinates are in the source heatmap image's pixel space; the image is
        // rendered letterboxed (contain) inside the container, so map through the drawn rect.
        const scale = Math.min(containerWidth / this.sourceWidth, containerHeight / this.sourceHeight);
        const offsetX = (containerWidth - this.sourceWidth * scale) / 2;
        const offsetY = (containerHeight - this.sourceHeight * scale) / 2;
        const iconSize = Math.max(MIN_ICON_SIZE, Math.round(BASE_ICON_SIZE * scale));

        this.markers.forEach((marker) => {
            const centerX = offsetX + marker.x * scale;
            const centerY = offsetY + marker.y * scale;
            this.drawMeshIcon(ctx, centerX, centerY, iconSize, marker.color);
        });
    }

    private drawMeshIcon(
        ctx: CanvasRenderingContext2D,
        centerX: number,
        centerY: number,
        size: number,
        color: string
    ): void {
        const iconScale = size / ICON_PATHS.mesh.viewBox;

        ctx.save();
        ctx.translate(centerX - size / 2, centerY - size / 2);
        ctx.scale(iconScale, iconScale);
        ctx.fillStyle = color;
        if (typeof Path2D !== 'undefined') {
            if (!cachedMeshPath) {
                cachedMeshPath = new Path2D(ICON_PATHS.mesh.path);
            }
            ctx.fill(cachedMeshPath);
        }
        ctx.restore();
    }
}
